import React, { useRef, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Paper, Button, Grid, Divider } from '@mui/material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export default function SalarySlipTemplate({ slip, hideActions }) {
  const slipRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const element = slipRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');

    // A4 size in mm
    const pdfWidth = 210;
    const pdfHeight = 297;
    // Image size in px to mm
    const imgWidth = pdfWidth - 20; // 10mm padding on each side
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const pdf = new jsPDF('p', 'mm', 'a4');
    const x = (pdfWidth - imgWidth) / 2;
    const y = 10; // top padding

    pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
    pdf.save('salary-slip.pdf');
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        body * { display: none !important; }
        .salary-slip-print { display: block !important; position: absolute !important; left: 0; top: 0; width: 100vw; background: white; box-shadow: none; }
        .salary-slip-actions { display: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  if (!slip) return null;

  return (
    <div>
      
      <Box>
        <Box ref={slipRef} className="salary-slip-print" sx={{
          p: 4,
          background: '#f7fafc',
          maxWidth: 900,
          mx: 'auto',
          borderRadius: 3,
          boxShadow: 3,
          border: '1px solid #e0e0e0',
          mb: 4
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <img src="/logo.png" alt="Company Logo" style={{ width: 80, height: 80, marginRight: 16 }} />
            <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: 1 }}>
              {slip.company}
            </Typography>
          </Box>
          <Typography align="center" sx={{ color: 'text.secondary', mb: 1 }}>{slip.address}</Typography>
          <Typography align="center" sx={{ fontWeight: 500, mb: 3, fontSize: 18, color: '#1976d2' }}>
            PAYSLIP FOR THE MONTH of <b>{slip.month} {slip.year}</b>
          </Typography>
          {/* Employee details, Attendance, Salary Transferred To in three columns */}
          <Box sx={{ background: '#f7fafc', px: 2, py: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Grid container>
              {/* Employee details */}
              <Grid item xs={12} md={4}>
                <Typography fontWeight={700} sx={{ color: '#f9a825', mb: 1 }}>Employee details</Typography>
                <Table size="small" sx={{ width: '100%' }}>
                  <TableBody>
                    <TableRow><TableCell sx={{ border: 0 }}>EID:</TableCell><TableCell sx={{ border: 0 }}>{slip.eid}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ border: 0 }}>Designation:</TableCell><TableCell sx={{ border: 0 }}>{slip.designation}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ border: 0 }}>Name:</TableCell><TableCell sx={{ border: 0 }}>{slip.name}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ border: 0 }}>Department:</TableCell><TableCell sx={{ border: 0 }}>IT</TableCell></TableRow>
                    <TableRow><TableCell sx={{ border: 0 }}>Month:</TableCell><TableCell sx={{ border: 0 }}>{slip.month}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </Grid>
              {/* Divider */}
              <Grid item md={0.05} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: '#e0e0e0' }} />
              </Grid>
              {/* Divider */}
              {/* Salary Transferred To */}
              <Grid item xs={12} md={4}>
                <Typography fontWeight={700} sx={{ color: '#388e3c', mb: 1 }}> Salary Transferred To</Typography>
                <Table size="small" sx={{ width: '100%' }}>
                  <TableBody>
                    <TableRow><TableCell sx={{ border: 0 }}>Bank Name:</TableCell><TableCell sx={{ border: 0 }}>{slip.bankName}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ border: 0 }}>Bank Account:</TableCell><TableCell sx={{ border: 0 }}>{slip.bankAccount}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </Grid>
              {/* Divider */}
              <Grid item md={0.05} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: '#e0e0e0' }} />
              </Grid>
              {/* Divider */}
              {/* Employee Attendance Summary */}
              <Grid item xs={12} md={4}>
                <Typography fontWeight={700} sx={{ color: '#1976d2', mb: 1 }}>Attendance Summary</Typography>
                <Table size="small" sx={{ width: '100%' }}>
                  <TableBody>
                    <TableRow><TableCell sx={{ border: 0 }}>Working Days:</TableCell><TableCell sx={{ border: 0 }}>{slip.workingDays}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ border: 0 }}>Leave Taken:</TableCell><TableCell sx={{ border: 0 }}>{slip.leaveTaken}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ border: 0 }}>Payable Days:</TableCell><TableCell sx={{ border: 0 }}>{slip.payableDays}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ border: 0 }}>Leave Balance:</TableCell><TableCell sx={{ border: 0 }}>{slip.leaveBalance}</TableCell></TableRow>
                    <TableRow><TableCell sx={{ border: 0 }}>Leave Allowed:</TableCell><TableCell sx={{ border: 0 }}>{slip.leaveAllowed}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          </Box>
          <Divider sx={{ my: 3 }} />
          <Typography fontWeight={700} sx={{ mb: 1, fontSize: 20, color: '#1976d2' }}>Salary Calculations</Typography>
          <TableContainer component={Paper} sx={{ mb: 2, background: '#f5f5f5', borderRadius: 2 }}>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ background: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Cost To Company - CTC</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>₹ {slip.ctc}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Deductions</TableCell>
                  <TableCell></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Actual Salary</TableCell>
                  <TableCell>₹ {slip.actualSalary}</TableCell>
                  <TableCell>TDS</TableCell>
                  <TableCell>₹ {slip.tds}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Basic</TableCell>
                  <TableCell>₹ {slip.basic}</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>House Rent Allowance</TableCell>
                  <TableCell>₹ {slip.hra}</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Special Allowance</TableCell>
                  <TableCell>₹ {slip.specialAllowance}</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
                {/* Conditionally render Food Allowance */}
                {Number(slip.foodAllowance) !== 0 && slip.foodAllowance !== undefined && slip.foodAllowance !== null && slip.foodAllowance !== '' && (
                  <TableRow>
                    <TableCell>Food Allowance</TableCell>
                    <TableCell>₹ {slip.foodAllowance}</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
                {/* Conditionally render Employee Engagement Fund */}
                {Number(slip.engagementFund) !== 0 && slip.engagementFund !== undefined && slip.engagementFund !== null && slip.engagementFund !== '' && (
                  <TableRow>
                    <TableCell>Employee Engagement Fund</TableCell>
                    <TableCell>₹ {slip.engagementFund}</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
                {/* Conditionally render Telephone And Internet Allow */}
                {Number(slip.telephone) !== 0 && slip.telephone !== undefined && slip.telephone !== null && slip.telephone !== '' && (
                  <TableRow>
                    <TableCell>Telephone And Internet Allow</TableCell>
                    <TableCell>₹ {slip.telephone}</TableCell>
                    <TableCell>Total Deductions</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>₹ {slip.totalDeductions}</TableCell>
                  </TableRow>
                )}
                {/* Conditionally render Total Allowance */}
                {Number(slip.totalAllowance) !== 0 && slip.totalAllowance !== undefined && slip.totalAllowance !== null && slip.totalAllowance !== '' && (
                  <TableRow>
                    <TableCell>Total Allowance</TableCell>
                    <TableCell>₹ {slip.totalAllowance}</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
                <TableRow sx={{ background: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Gross Salary</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>₹ {slip.gross}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#388e3c' }}>Net Payable Salary</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#388e3c' }}>₹ {slip.net}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography sx={{ mb: 2, fontWeight: 500, fontSize: 16 }}>
            Amount in Words: <span style={{ color: '#1976d2', fontWeight: 600 }}>{slip.amountWords || ''}</span>
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 2 }}>
            This is a system-generated salary slip and does not require a signature.
          </Typography>
        </Box>
        {!hideActions && (
          <Box className="salary-slip-actions" sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleDownloadPDF}>Download PDF</Button>
            <Button variant="outlined" onClick={handlePrint}>Print</Button>
          </Box>
        )}
      </Box>
    </div>
  );
} 